import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { AlarmSoundKey, primeAlarmAudio, triggerAlarm } from '@/lib/task-alarm';

export interface AlarmConfig {
  alarm_enabled?: boolean | null;
  alarm_sound?: string | null;
  alarm_vibrate?: boolean | null;
  alarm_repeat_minutes?: number | null;
  alarm_max_repeats?: number | null;
}

export interface AlarmTaskInput {
  id: string;
  scheduled_for: string;
  isCompleted: boolean;
  name: string;
}

export interface RingingAlarm {
  instanceId: string;
  name: string;
  scheduledFor: string;
  repeatIndex: number;
}

const TICK_MS = 15_000;

export function useTaskAlarms(params: {
  config: AlarmConfig | null | undefined;
  tasks: AlarmTaskInput[];
  active: boolean;
  storageKey: string;
}) {
  const { config, tasks, active, storageKey } = params;

  const enabled = !!config?.alarm_enabled && active;
  const sound = (config?.alarm_sound as AlarmSoundKey) || 'bell';
  const vibrate = config?.alarm_vibrate !== false;
  const repeatMinutes = Math.max(1, config?.alarm_repeat_minutes ?? 5);
  const maxRepeats = Math.max(0, config?.alarm_max_repeats ?? 3);

  const [ringing, setRinging] = useState<RingingAlarm | null>(null);
  const firedRef = useRef<Set<string>>(new Set());

  // Restore already-fired markers so a page refresh doesn't replay every alarm.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) firedRef.current = new Set(JSON.parse(raw) as string[]);
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  const persistFired = useCallback(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify([...firedRef.current].slice(-200)));
    } catch {
      /* ignore */
    }
  }, [storageKey]);

  // Unlock audio on the first user interaction (mobile autoplay policies).
  useEffect(() => {
    if (!enabled) return;
    const unlock = () => primeAlarmAudio();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [enabled]);

  const pendingTasks = useMemo(
    () => tasks.filter((task) => !task.isCompleted),
    [tasks],
  );

  // Foreground alarm loop.
  useEffect(() => {
    if (!enabled) {
      setRinging(null);
      return;
    }

    const check = () => {
      const now = Date.now();
      let candidate: RingingAlarm | null = null;

      for (const task of pendingTasks) {
        const scheduled = new Date(task.scheduled_for).getTime();
        if (Number.isNaN(scheduled) || scheduled > now) continue;

        const elapsedMin = (now - scheduled) / 60_000;
        const repeatIndex = Math.min(
          maxRepeats,
          Math.floor(elapsedMin / repeatMinutes),
        );

        // Don't ring for tasks that are way past the alarm window.
        const windowMin = repeatMinutes * (maxRepeats + 1) + 10;
        if (elapsedMin > windowMin) continue;

        const key = `${task.id}:${repeatIndex}`;
        if (firedRef.current.has(key)) continue;

        firedRef.current.add(key);
        candidate = {
          instanceId: task.id,
          name: task.name,
          scheduledFor: task.scheduled_for,
          repeatIndex,
        };
      }

      if (candidate) {
        persistFired();
        triggerAlarm(sound, vibrate);
        setRinging(candidate);
      }
    };

    check();
    const interval = setInterval(check, TICK_MS);
    return () => clearInterval(interval);
  }, [enabled, pendingTasks, repeatMinutes, maxRepeats, sound, vibrate, persistFired]);

  const dismiss = useCallback(() => setRinging(null), []);

  const snooze = useCallback(() => {
    // Clear the fired marker for the *next* repeat so it rings again on schedule.
    setRinging(null);
  }, []);

  return { ringing, dismiss, snooze, alarmEnabled: enabled, sound, vibrate };
}

